import React, { useMemo, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import * as THREE from 'three';
import { useFlowaesStore } from '../store/useFlowaesStore';
import { appProfiler } from '../utils/appProfiler';

interface InstancedCandlesProps {
  candles: any[];
  width: number;
  startX: number;
  wallZ: number;
  minP: number;
  maxP: number;
  heightScale3D: number;
  opacity?: number;
  clippingPlanes?: THREE.Plane[];
}

const MAX_CANDLES = 50000;

const InstancedCandles = forwardRef<any, InstancedCandlesProps>(({

  candles,
  width,
  startX,
  wallZ,
  minP,
  maxP,
  heightScale3D,
  opacity = 0.4,
  clippingPlanes,
}, ref) => {
  const colorUp = useFlowaesStore(state => state.colorUp);
  const colorDown = useFlowaesStore(state => state.colorDown);

  const meshRef = useRef<THREE.InstancedMesh>(null);
  const geomRef = useRef<THREE.BoxGeometry>(null);
  const matRef = useRef<THREE.MeshBasicMaterial>(null);
  
  const wickRef = useRef<THREE.InstancedMesh>(null);
  const wickGeomRef = useRef<THREE.BoxGeometry>(null);
  const wickMatRef = useRef<THREE.MeshBasicMaterial>(null);

  const prevCandlesRef = useRef<any[] | null>(null);
  const prevLength = useRef<number>(0);
  const prevWidth = useRef<number>(width);
  const prevStartX = useRef<number>(startX);
  const prevWallZ = useRef<number>(wallZ);
  const prevColorUp = useRef(colorUp);
  const prevColorDown = useRef(colorDown);
  const prevDataRef = useRef<any>(null);



  const updateMesh = (windowData: any[], currentWidth: number, currentStartX: number, currentWallZ: number) => {
      if (!meshRef.current || !wickRef.current) return;
      const count = windowData.length;
      const dummy = new THREE.Object3D();
      const candleWidth = (currentWidth / Math.max(1, count)) * 0.6;
      const wickWidth = Math.max(candleWidth * 0.15, 0.2);
      const safePriceRange = Math.max(maxP - minP, 0.0001);
      const scaleY = heightScale3D / safePriceRange;
      const minBodyRaw = 0.5 / scaleY;
      const minWickRaw = 0.1 / scaleY;
      
      for (let i = 0; i < count; i++) {
        const c = windowData[i];
        if (!c) continue;
        const x = currentStartX + (i / Math.max(1, count - 1)) * currentWidth;
        const yOpen = c.open ?? c.price;
        const yClose = c.close ?? c.price;
        const yHigh = c.high ?? c.price;
        const yLow = c.low ?? c.price;
        const isGreen = yClose >= yOpen;
        
        // Body
        const bodyHeight = Math.max(Math.abs(yClose - yOpen), minBodyRaw);
        const bodyY = Math.min(yOpen, yClose) + bodyHeight / 2;
        dummy.position.set(x, bodyY, currentWallZ);
        dummy.scale.set(candleWidth, bodyHeight, 1);
        dummy.updateMatrix();
        meshRef.current.setMatrixAt(i, dummy.matrix);
        meshRef.current.setColorAt(i, isGreen ? colors.up : colors.down);
        
        // Wick
        const wickHeight = Math.max(yHigh - yLow, minWickRaw);
        const wickY = yLow + wickHeight / 2;
        dummy.position.set(x, wickY, currentWallZ);
        dummy.scale.set(wickWidth, wickHeight, 1);
        dummy.updateMatrix();
        wickRef.current.setMatrixAt(i, dummy.matrix);
        wickRef.current.setColorAt(i, isGreen ? colors.up : colors.down);
      }
      
      meshRef.current.count = count;
      wickRef.current.count = count;
      meshRef.current.instanceMatrix.needsUpdate = true;
      if (meshRef.current.instanceColor) meshRef.current.instanceColor.needsUpdate = true;
      wickRef.current.instanceMatrix.needsUpdate = true;
      if (wickRef.current.instanceColor) wickRef.current.instanceColor.needsUpdate = true;
  };




  useImperativeHandle(ref, () => ({
    updateWindow: (windowData: any[], currentWidth: number, currentStartX: number, currentWallZ: number) => {
      updateMesh(windowData, currentWidth, currentStartX, currentWallZ);
    }
  }));


  useEffect(() => {
    return () => {
      geomRef.current?.dispose();
      matRef.current?.dispose();
      wickGeomRef.current?.dispose();
      wickMatRef.current?.dispose();
    };
  }, []);

  const colors = useMemo(() => {
    return {
      up: new THREE.Color(colorUp),
      down: new THREE.Color(colorDown),
    };
  }, [colorUp, colorDown]);

  // Apply raw space transformation
  useEffect(() => {
    if (!meshRef.current || !wickRef.current) return;
    
    // Protect against division by zero
    const safePriceRange = Math.max(maxP - minP, 0.0001);
    const scaleY = heightScale3D / safePriceRange;
    const posY = -minP * scaleY - (heightScale3D / 2);

    meshRef.current.scale.y = scaleY;
    meshRef.current.position.y = posY;
    
    wickRef.current.scale.y = scaleY;
    wickRef.current.position.y = posY;
  }, [minP, maxP, heightScale3D]);

  useEffect(() => {
    if (!meshRef.current || !wickRef.current) return;

    const candleCount = candles.length;
    if (candleCount === 0) {
      meshRef.current.count = 0;
      wickRef.current.count = 0;
      prevLength.current = 0;
      return;
    }

    const dummy = new THREE.Object3D();
    const candleWidth = (width / candleCount) * 0.6;
    const wickWidth = Math.max(candleWidth * 0.15, 0.2);

    const safePriceRange = Math.max(maxP - minP, 0.0001);
    const scaleY = heightScale3D / safePriceRange;
    
    // We want the minimum visual height to be 0.5 pixels for bodies, 0.1 for wicks.
    // Convert minimum pixel height back to raw price height.
    const minBodyRaw = 0.5 / scaleY;
    const minWickRaw = 0.1 / scaleY;

    // Check if the dataset identity has changed (new array reference, symbol/timeframe switch)
    const isNewDataset = prevCandlesRef.current !== candles;

    const needsFullUpdate = 
      isNewDataset ||
      candleCount < prevLength.current ||
      width !== prevWidth.current ||
      startX !== prevStartX.current ||
      wallZ !== prevWallZ.current ||
      colorUp !== prevColorUp.current ||
      colorDown !== prevColorDown.current;

    const updateCandle = (c: any, i: number) => {
      const x = startX + (i / Math.max(1, (candleCount - 1))) * width;
      
      const yOpen = c.open;
      const yClose = c.close;
      const yHigh = c.high;
      const yLow = c.low;

      const isGreen = c.close >= c.open;

      // Body
      const bodyHeight = Math.max(Math.abs(yClose - yOpen), minBodyRaw);
      const bodyY = Math.min(yOpen, yClose) + bodyHeight / 2;
      
      dummy.position.set(x, bodyY, wallZ);
      dummy.scale.set(candleWidth, bodyHeight, 1);
      dummy.updateMatrix();
      
      meshRef.current!.setMatrixAt(i, dummy.matrix);
      meshRef.current!.setColorAt(i, isGreen ? colors.up : colors.down);

      // Wick
      const wickHeight = Math.max(yHigh - yLow, minWickRaw);
      const wickY = yLow + wickHeight / 2;
      
      dummy.position.set(x, wickY, wallZ);
      dummy.scale.set(wickWidth, wickHeight, 1);
      dummy.updateMatrix();
      
      wickRef.current!.setMatrixAt(i, dummy.matrix);
      wickRef.current!.setColorAt(i, isGreen ? colors.up : colors.down);
    };

    const setUpdateRange = (attr: THREE.BufferAttribute | THREE.InstancedBufferAttribute | null, offset: number, count: number) => {
      if (!attr) return;
      if (typeof attr.clearUpdateRanges === 'function') {
        attr.clearUpdateRanges();
        attr.addUpdateRange(offset, count);
      } else if ('updateRange' in attr) {
        (attr as any).updateRange = { offset, count };
      }
    };

    appProfiler.time('WebGL:InstancedCandles:BufferUpload', () => {
      if (needsFullUpdate) {
        // Update all candles
        for (let i = 0; i < candleCount; i++) {
          updateCandle(candles[i], i);
        }
        setUpdateRange(meshRef.current!.instanceMatrix, 0, candleCount * 16);
        setUpdateRange(meshRef.current!.instanceColor, 0, candleCount * 3);
        setUpdateRange(wickRef.current!.instanceMatrix, 0, candleCount * 16);
        setUpdateRange(wickRef.current!.instanceColor, 0, candleCount * 3);
      } else {
        if (candleCount === prevLength.current) {
          // Only update the last one
          updateCandle(candles[candleCount - 1], candleCount - 1);
          setUpdateRange(meshRef.current!.instanceMatrix, (candleCount - 1) * 16, 16);
          setUpdateRange(meshRef.current!.instanceColor, (candleCount - 1) * 3, 3);
          setUpdateRange(wickRef.current!.instanceMatrix, (candleCount - 1) * 16, 16);
          setUpdateRange(wickRef.current!.instanceColor, (candleCount - 1) * 3, 3);
        } else {
          // Update new candles + the last previous candle if it finalized
          const startIdx = Math.max(0, prevLength.current - 1);
          for (let i = startIdx; i < candleCount; i++) {
            updateCandle(candles[i], i);
          }
          const countToUpdate = candleCount - startIdx;
          setUpdateRange(meshRef.current!.instanceMatrix, startIdx * 16, countToUpdate * 16);
          setUpdateRange(meshRef.current!.instanceColor, startIdx * 3, countToUpdate * 3);
          setUpdateRange(wickRef.current!.instanceMatrix, startIdx * 16, countToUpdate * 16);
          setUpdateRange(wickRef.current!.instanceColor, startIdx * 3, countToUpdate * 3);
        }
      }

      meshRef.current!.count = candleCount;
      wickRef.current!.count = candleCount;
      
      meshRef.current!.instanceMatrix.needsUpdate = true;
      if (meshRef.current!.instanceColor) meshRef.current!.instanceColor.needsUpdate = true;
      wickRef.current!.instanceMatrix.needsUpdate = true;
      if (wickRef.current!.instanceColor) wickRef.current!.instanceColor.needsUpdate = true;
    });

    prevCandlesRef.current = candles;
    prevLength.current = candleCount;
    prevWidth.current = width;
    prevStartX.current = startX;
    prevWallZ.current = wallZ;
    prevColorUp.current = colorUp;
    prevColorDown.current = colorDown;
  }, [candles, startX, width, wallZ, colorUp, colorDown, colors, minP, maxP, heightScale3D]);

  useEffect(() => {
    const planes = (clippingPlanes && clippingPlanes.length > 0) ? clippingPlanes : null;
    if (matRef.current) {
      matRef.current.clippingPlanes = planes;
      matRef.current.needsUpdate = true;
    }
    if (wickMatRef.current) {
      wickMatRef.current.clippingPlanes = planes;
      wickMatRef.current.needsUpdate = true;
    }
  }, [clippingPlanes]);

  const activePlanes = (clippingPlanes && clippingPlanes.length > 0) ? clippingPlanes : null;

  return (
    <group>
      <instancedMesh ref={meshRef} args={[undefined, undefined, MAX_CANDLES]} frustumCulled={false}>
        <boxGeometry ref={geomRef} args={[1, 1, 0.1]} />
        <meshBasicMaterial ref={matRef} toneMapped={false} transparent opacity={opacity} clippingPlanes={activePlanes} />
      </instancedMesh>
      <instancedMesh ref={wickRef} args={[undefined, undefined, MAX_CANDLES]} frustumCulled={false}>
        <boxGeometry ref={wickGeomRef} args={[1, 1, 0.1]} />
        <meshBasicMaterial ref={wickMatRef} toneMapped={false} transparent opacity={opacity} clippingPlanes={activePlanes} />
      </instancedMesh>
    </group>
  );
});

export default InstancedCandles;
